/**
 * Lightweight Web Audio player — replaces the `use-sound` (Howler) dependency.
 *
 * Why we moved off use-sound/Howler:
 *   - Howler creates an AudioContext that gets suspended when a tab is hidden;
 *     resuming it on a throttled background tick adds nondeterministic latency
 *     to sound playback, which aggravated the "Blind Up fires 10s late" bug.
 *   - We only need to play one short mp3 with no advanced features (no sprite,
 *     no fading, no rate control). Native Web Audio is ~40 lines and has zero
 *     suspension/scheduling surprises.
 *
 * Usage:
 *   const player = createSoundPlayer()
 *   await player.load('level', '/level.mp3')
 *   player.play('level')
 *   player.stop('level')  // cancel anything currently sounding / queued
 *
 * Notes:
 *   - AudioContext must be created/resumed after a user gesture. We lazily
 *     create it on the first play() call (which is always triggered by a timer
 *     event after the user has already clicked into the timer page).
 *   - play() returns true if playback actually started, false if blocked
 *     (no buffer, context suspended, etc.) so callers can log/telemetry.
 */

let sharedContext = null

const getContext = () => {
	if (sharedContext) return sharedContext
	const AC = typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : null
	if (!AC) return null
	try {
		sharedContext = new AC()
	} catch {
		sharedContext = null
	}
	return sharedContext
}

/**
 * Resume the AudioContext after a user gesture. Safe to call repeatedly.
 * Browsers auto-suspend AudioContext until a gesture occurs; calling this on
 * the timer page's first interaction unlocks playback.
 */
export const unlockAudio = async () => {
	const ctx = getContext()
	if (!ctx) return
	if (ctx.state === 'suspended') {
		try {
			await ctx.resume()
		} catch {
			// ignore — will retry on next play()
		}
	}
}

export const createSoundPlayer = () => {
	/** @type {Map<string, AudioBuffer>} */
	const buffers = new Map()
	/** @type {Map<string, {source: AudioBufferSourceNode, gain: GainNode, startedAt: number}>} */
	const active = new Map()

	/**
	 * Fetch + decode an audio file into a buffer.
	 * @param {string} key
	 * @param {string} url
	 */
	const load = async (key, url) => {
		if (buffers.has(key)) return buffers.get(key)
		const ctx = getContext()
		if (!ctx) {
			console.warn('🚀~ soundPlayer: Web Audio not available')
			return null
		}
		try {
			const res = await fetch(url)
			const arr = await res.arrayBuffer()
			// decodeAudioData with callback fallback for older Safari signatures
			const buf = await new Promise((resolve, reject) => {
				const p = ctx.decodeAudioData(arr, resolve, reject)
				if (p && typeof p.then === 'function') p.then(resolve, reject)
			})
			buffers.set(key, buf)
			return buf
		} catch (err) {
			console.warn(`🚀~ soundPlayer: failed to load "${key}" from ${url}`, err)
			return null
		}
	}

	/**
	 * @param {string} key
	 * @param {{volume?: number}} [opts]
	 * @returns {boolean} whether playback started
	 */
	const play = (key, opts) => {
		const buf = buffers.get(key)
		if (!buf) {
			console.warn(`🚀~ soundPlayer: no buffer for "${key}"`)
			return false
		}
		const ctx = getContext()
		if (!ctx) return false
		if (ctx.state === 'suspended') {
			// Try to resume; if it stays suspended (no gesture yet), bail out
			// rather than queueing a sound that fires late — the root behavior
			// we are trying to eliminate.
			ctx.resume().catch(() => {})
			if (ctx.state === 'suspended') {
				console.warn('🚀~ soundPlayer: AudioContext suspended, skipping play')
				return false
			}
		}

		// If the same key is already sounding, let it finish (don't stack identical
		// sounds). Use stop() first if you want to retrigger.
		if (active.has(key)) return false

		const source = ctx.createBufferSource()
		source.buffer = buf
		const gain = ctx.createGain()
		const volume = typeof opts?.volume === 'number' ? opts.volume : 1
		gain.gain.value = volume
		source.connect(gain).connect(ctx.destination)
		source.onended = () => {
			const cur = active.get(key)
			if (cur && cur.source === source) active.delete(key)
		}
		source.start(0)
		active.set(key, { source, gain, startedAt: ctx.currentTime })
		return true
	}

	/**
	 * Stop any currently playing instance of a key immediately. Used when the
	 * tab loses leadership or becomes hidden, so a sound scheduled late does
	 * not actually emit.
	 */
	const stop = (key) => {
		const cur = active.get(key)
		if (!cur) return
		try {
			cur.source.onended = null
			cur.source.stop(0)
		} catch {
			// already stopped
		}
		active.delete(key)
	}

	/** Stop everything. */
	const stopAll = () => {
		for (const key of Array.from(active.keys())) stop(key)
	}

	const dispose = () => {
		stopAll()
		buffers.clear()
	}

	return { load, play, stop, stopAll, dispose }
}
